import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
	Dialog,
	DialogContent,
	Drawer,
	Typography,
	Button,
	Box,
	Stack,
	TextField,
	FormControl,
	Select,
	MenuItem,
	CircularProgress,
	Alert,
	IconButton,
	InputAdornment,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import { supabase } from '../utils/supabase'
import posthog, { isPostHogConfigured } from '../posthog'
import { getActiveMethods } from '../utils/paymentMethodUtils'
import { getCurrencySymbol } from '../utils/currencyUtils'
import { splitStoredAddOns, distributeAddOns } from '../utils/checkoutUtils'
import MobileCheckout from './appointment-details/MobileCheckout'
import PaymentIcon from '@mui/icons-material/Payment'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import AddIcon from '@mui/icons-material/Add'
import RemoveIcon from '@mui/icons-material/Remove'
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import SquareCardDialog from './SquareCardDialog'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import { getServicePrice } from '../utils/appointmentUtils'

dayjs.extend(utc)
dayjs.extend(timezone)

const TERMINAL_POLL_INTERVAL_MS = 3500
const TERMINAL_TIMEOUT_MS = 3 * 60 * 1000 // 3 minutes

// Lavender design tokens
const L = {
	bg: '#FDFCFF',
	surface: '#F5F3FB',
	border: '#E7E2F3',
	text: '#2E2A40',
	muted: '#8B85A4',
	primary: '#7C6BD9',
	primaryDeep: '#5F4EC2',
	primarySoft: '#EDE9FB',
	field: '#FFFFFF',
	r: '14px',
	rSm: '10px',
	font: "'Outfit', sans-serif",
}

function SectionTitle({ children, hint }) {
	return (
		<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '8px' }}>
			<Typography sx={{
				fontSize: '11.5px', fontWeight: 700, textTransform: 'uppercase',
				letterSpacing: '0.08em', color: L.primaryDeep, fontFamily: L.font,
			}}>
				{children}
			</Typography>
			{hint && (
				<Typography sx={{
					fontSize: '10.5px', fontWeight: 600, color: L.muted,
					letterSpacing: '0.04em', fontFamily: L.font,
				}}>
					· {hint}
				</Typography>
			)}
		</Box>
	)
}

export default function LogPaymentDialog({ open, onClose, appointment, merchant, onPaymentLogged }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	// Inject Outfit font
	useEffect(() => {
		const id = 'outfit-font-link'
		if (!document.getElementById(id)) {
			const link = document.createElement('link')
			link.id = id
			link.rel = 'stylesheet'
			link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap'
			document.head.appendChild(link)
		}
	}, [])

	// Form state
	const [addOns, setAddOns] = useState([])
	const [addOnCounts, setAddOnCounts] = useState({})
	// Stored add-ons that no longer match the add-on catalog (deleted service,
	// is_add_on turned off, missing service_id). They keep their booked price
	// and stay in the totals — dropping them made checkout show less than the
	// scheduled amount.
	const [legacyAddOns, setLegacyAddOns] = useState([])
	const [addOnSearch, setAddOnSearch] = useState('')
	const [paymentMethod, setPaymentMethod] = useState(() => getActiveMethods(merchant?.payment_methods)[0]?.value ?? 'cash')
	// Tip (design "A · Lavender" checkout): 15/18/25% chips or a custom
	// percent / fixed dollar amount. Tapping an active chip clears it.
	const [selectedTipOption, setSelectedTipOption] = useState(null)
	const [tipUnit, setTipUnit] = useState('percent') // custom only: 'percent' | 'fixed'
	const [customTipAmount, setCustomTipAmount] = useState('')
	// Discount (design "A · Lavender" checkout): 10/15/25% chips or a custom
	// percent / fixed dollar amount. Tapping an active chip clears it.
	const [selectedDiscountOption, setSelectedDiscountOption] = useState(null) // '10' | '15' | '25' | 'custom' | null
	const [discountUnit, setDiscountUnit] = useState('percent') // custom only: 'percent' | 'fixed'
	const [customDiscountValue, setCustomDiscountValue] = useState('')
	const [notes, setNotes] = useState('')
	const [loading, setLoading] = useState(false)
	const [saving, setSaving] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')
	const [serviceInfo, setServiceInfo] = useState(null)
	const [groupedAppointments, setGroupedAppointments] = useState([])
	const [client, setClient] = useState(null)

	const [terminalStatus, setTerminalStatus] = useState(null)
	const pollIntervalRef = useRef(null)
	const pollTimeoutRef = useRef(null)
	const savePaymentToDbRef = useRef(null)

	const hasSquareTerminal = Boolean(merchant?.square_terminal_device_id)
	const hasSquareConnected = Boolean(merchant?.square_access_token)
	const isSquareTerminalMethod = paymentMethod === 'square_terminal'
	const isSquareCardMethod = paymentMethod === 'square_card'

	const [squareCardOpen, setSquareCardOpen] = useState(false)
	const [squareCardIdempotencyKey, setSquareCardIdempotencyKey] = useState('')

	// Mobile "Payment complete" screen (design: Mobile · checkout → paid).
	// On mobile a successful payment shows this view instead of closing;
	// onPaymentLogged is deferred until "Back to appointment" / close so the
	// appointment-details screen behind flips to Completed at that point.
	const [paidInfo, setPaidInfo] = useState(null) // { amount, method, when } | null

	const availablePaymentMethods = useMemo(() => {
		let methods = getActiveMethods(merchant?.payment_methods)

		if (!hasSquareTerminal) {
			methods = methods.filter(m => m.value !== 'square_terminal')
		} else if (!methods.find(m => m.value === 'square_terminal')) {
			methods = [...methods, { value: 'square_terminal', label: 'Square Terminal' }]
		}

		if (!hasSquareConnected) {
			methods = methods.filter(m => m.value !== 'square_card')
		}

		return methods
	}, [merchant?.payment_methods, hasSquareTerminal, hasSquareConnected])

	const loadData = useCallback(async () => {
		if (!merchant?.id || !appointment?.service_id) return

		setLoading(true)
		try {
			let groupData = null

			if (appointment.group_id) {
				const { data, error: groupError } = await supabase
					.from('appointments')
					.select(`
						id,
						service_id,
						pet_id,
						start_ts,
						end_ts,
						add_ons,
						is_group_primary,
						service_price,
						service_types(id, name, price),
						pets(id, name)
					`)
					.eq('group_id', appointment.group_id)
					.order('start_ts')

				if (groupError) throw groupError
				groupData = data || []
				setGroupedAppointments(groupData)
				setServiceInfo(null)
			} else {
				const { data: serviceData, error: serviceError } = await supabase
					.from('service_types')
					.select('id, name, price, duration_minutes')
					.eq('id', appointment.service_id)
					.single()

				if (serviceError) throw serviceError
				setServiceInfo(serviceData)
				setGroupedAppointments([])
			}

			const { data: addOnsData, error: addOnsError } = await supabase
				.from('service_types')
				.select('id, name, price, duration_minutes')
				.eq('merchant_id', merchant.id)
				.eq('is_add_on', true)
				.order('name')

			if (addOnsError) throw addOnsError
			setAddOns(addOnsData || [])

			if (appointment?.user_id) {
				const { data: clientData } = await supabase
					.from('clients')
					.select('id, name, last_name, email, phone_number, square_customer_id, square_card_id, square_card_brand, square_card_last4')
					.eq('id', appointment.user_id)
					.single()
				setClient(clientData || null)
			} else {
				setClient(null)
			}

			// Seed the picker from what's already on the appointment row(s):
			// catalog matches become counts, everything else becomes a legacy
			// line item at its stored price — so the opening checkout total
			// always equals the scheduled total.
			const targetAppointments = groupData && groupData.length > 0
				? groupData
				: [{ id: appointment.id, add_ons: appointment.add_ons }]
			const { counts, legacy } = splitStoredAddOns(targetAppointments, addOnsData || [])
			setAddOnCounts(counts)
			setLegacyAddOns(legacy)
		} catch (e) {
			console.error('Error loading data:', e)
			setErrorMessage('Failed to load payment data: ' + e.message)
		} finally {
			setLoading(false)
		}
	}, [merchant?.id, appointment?.id, appointment?.service_id, appointment?.group_id, appointment?.add_ons, appointment?.user_id])

	useEffect(() => {
		if (open && merchant?.id) {
			loadData()
		}
	}, [open, merchant?.id, loadData])

	const stopPolling = useCallback(() => {
		if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
		if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
		pollIntervalRef.current = null
		pollTimeoutRef.current = null
	}, [])

	useEffect(() => {
		return () => stopPolling()
	}, [stopPolling])

	useEffect(() => {
		if (!open) {
			stopPolling()
			setTerminalStatus(null)
			setSquareCardOpen(false)
			setSquareCardIdempotencyKey('')
			setAddOnCounts({})
			setLegacyAddOns([])
			setAddOnSearch('')
			setPaymentMethod(getActiveMethods(merchant?.payment_methods)[0]?.value ?? 'cash')
			setSelectedTipOption(null)
			setTipUnit('percent')
			setCustomTipAmount('')
			setSelectedDiscountOption(null)
			setDiscountUnit('percent')
			setCustomDiscountValue('')
			setNotes('')
			setErrorMessage('')
			setServiceInfo(null)
			setGroupedAppointments([])
			setClient(null)
			setPaidInfo(null)
		}
	}, [open, merchant?.payment_methods, stopPolling])

	const serviceAmount = groupedAppointments.length > 0
		? groupedAppointments.reduce((sum, appt) => sum + getServicePrice(appt), 0)
		: getServicePrice(appointment, serviceInfo)

	const legacyAddOnsTotal = legacyAddOns.reduce((total, l) => total + (l.price || 0), 0)

	const addOnsTotal = Object.entries(addOnCounts).reduce((total, [addOnId, count]) => {
		const addon = addOns.find(a => a.id === addOnId)
		if (addon && count > 0) return total + (addon.price * count)
		return total
	}, 0) + legacyAddOnsTotal

	const subtotal = serviceAmount + addOnsTotal
	const taxRate = merchant?.tax_rate || 0

	// Discount math (design post-it): the discount applies to the subtotal;
	// tax and percent tips are then calculated on the discounted subtotal.
	const isFixedDiscount = selectedDiscountOption === 'custom' && discountUnit === 'fixed'
	const discountPercent = selectedDiscountOption === 'custom'
		? (isFixedDiscount ? 0 : (parseFloat(customDiscountValue) || 0))
		: (selectedDiscountOption ? Number(selectedDiscountOption) : 0)
	const rawDiscountCents = isFixedDiscount
		? Math.round((parseFloat(customDiscountValue) || 0) * 100)
		: Math.round((subtotal * discountPercent) / 100)
	const discountAmountCents = Math.min(subtotal, Math.max(0, rawDiscountCents))
	const discountedSubtotal = subtotal - discountAmountCents

	const taxAmount = Math.round((discountedSubtotal * taxRate) / 100)

	let tipsAmountCents = 0
	if (selectedTipOption === '15') tipsAmountCents = Math.round(discountedSubtotal * 0.15)
	else if (selectedTipOption === '18') tipsAmountCents = Math.round(discountedSubtotal * 0.18)
	else if (selectedTipOption === '25') tipsAmountCents = Math.round(discountedSubtotal * 0.25)
	else if (selectedTipOption === 'custom') {
		tipsAmountCents = tipUnit === 'fixed'
			? Math.round((parseFloat(customTipAmount) || 0) * 100)
			: Math.round(discountedSubtotal * (parseFloat(customTipAmount) || 0) / 100)
	}
	tipsAmountCents = Math.max(0, tipsAmountCents)

	const totalAmount = discountedSubtotal + taxAmount + tipsAmountCents

	// Summary label: "Tip (15%)" for preset percents, plain "Tip" for custom.
	const tipLabel = ['15', '18', '25'].includes(selectedTipOption)
		? `Tip (${selectedTipOption}%)`
		: 'Tip'

	// Summary label: "Discount (10%)" for percent discounts, plain "Discount"
	// for fixed dollar amounts.
	const discountLabel = !isFixedDiscount && discountPercent > 0
		? `Discount (${discountPercent % 1 ? discountPercent.toFixed(1) : discountPercent}%)`
		: 'Discount'

	const cs = getCurrencySymbol(merchant?.currency)
	const formatCurrency = (cents) => `${cs}${(cents / 100).toFixed(2)}`

	// After a successful save: desktop notifies the parent and closes; mobile
	// shows the "Payment complete" view and defers both until dismissal.
	const finalizePayment = useCallback(() => {
		if (isMobile) {
			const methodLabel = availablePaymentMethods.find(m => m.value === paymentMethod)?.label || paymentMethod
			setPaidInfo({
				amount: totalAmount,
				method: methodLabel,
				when: dayjs().tz(merchant?.timezone || 'UTC').format('h:mm A'),
			})
			setTerminalStatus(null)
			return
		}
		onPaymentLogged?.()
		onClose()
	}, [isMobile, availablePaymentMethods, paymentMethod, totalAmount, merchant?.timezone, onPaymentLogged, onClose])

	// Stable ref so the terminal polling interval always finalizes with the
	// latest totals/method without re-arming the interval.
	const finalizePaymentRef = useRef(finalizePayment)
	useEffect(() => {
		finalizePaymentRef.current = finalizePayment
	}, [finalizePayment])

	const savePaymentToDb = useCallback(async () => {
		if (!appointment?.id) throw new Error('Invalid appointment')
		if (!merchant?.id) throw new Error('Invalid merchant')

		const selectedAddOnsList = []
		Object.entries(addOnCounts).forEach(([addOnId, count]) => {
			const addon = addOns.find(a => a.id === addOnId)
			if (addon && count > 0) {
				for (let i = 0; i < count; i++) selectedAddOnsList.push(addon)
			}
		})

		// The payment records the full selection: picker counts at catalog
		// price plus legacy items at their stored (booked) price.
		const paymentAddOnsData = [
			...selectedAddOnsList.map(addon => ({
				service_id: addon.id,
				name: addon.name,
				amount: addon.price,
			})),
			...legacyAddOns.map(l => ({
				service_id: l.service_id,
				name: l.name,
				amount: l.price,
			})),
		]

		const paymentData = {
			merchant_id: merchant.id,
			appointment_id: appointment.id,
			client_id: appointment.user_id || null,
			group_id: appointment.group_id || null,
			payment_method: paymentMethod,
			service_amount: serviceAmount,
			add_ons: paymentAddOnsData,
			discount_type: discountAmountCents > 0 ? (isFixedDiscount ? 'fixed' : 'percent') : null,
			discount_value: discountAmountCents > 0
				? (isFixedDiscount ? (parseFloat(customDiscountValue) || 0) : discountPercent)
				: null,
			discount_amount: discountAmountCents,
			tips_amount: tipsAmountCents,
			tax_amount: taxAmount,
			total_amount: totalAmount,
			payment_status: 'completed',
			notes: notes.trim() || null,
		}

		// Order matters: the appointments are written FIRST. Their invoice
		// trigger (sync_pending_invoice, migration 35) rebuilds the open
		// invoice's appt_snapshot from the rows, so add-ons picked at checkout
		// land on the invoice document. Once the payments row is 'completed'
		// the trigger leaves it alone, so writing the payment first would
		// freeze a snapshot that is missing those add-ons.
		if (appointment.group_id && groupedAppointments.length > 0) {
			// Distribute the selection across the group WITHOUT duplication:
			// each appointment keeps its own still-selected add-ons, legacy
			// items stay where they were, and anything newly added at checkout
			// lands once, on the primary appointment. Writing the whole list to
			// every row (the old behavior) multiplied the add-ons by the group
			// size in every view that sums the rows.
			const primaryId = (
				groupedAppointments.find(a => a.is_group_primary) ||
				groupedAppointments.find(a => a.id === appointment.id) ||
				groupedAppointments[0]
			).id
			const byAppointment = distributeAddOns({
				appointmentsList: groupedAppointments,
				catalog: addOns,
				counts: addOnCounts,
				legacy: legacyAddOns,
				primaryId,
			})
			const results = await Promise.all(groupedAppointments.map(appt => {
				const list = byAppointment[appt.id] || []
				return supabase
					.from('appointments')
					.update({ status: 'completed', add_ons: list.length > 0 ? list : null })
					.eq('id', appt.id)
			}))
			const failed = results.find(r => r.error)
			if (failed) throw failed.error
		} else {
			const appointmentAddOnsData = distributeAddOns({
				appointmentsList: [appointment],
				catalog: addOns,
				counts: addOnCounts,
				legacy: legacyAddOns,
				primaryId: appointment.id,
			})[appointment.id] || []

			const addOnsDuration = appointmentAddOnsData.reduce((sum, a) => sum + (a.duration_minutes || 0), 0)
			const totalDuration = (serviceInfo?.duration_minutes || 0) + addOnsDuration
			const newEndTs = dayjs(appointment.start_ts)
				.tz(merchant?.timezone || 'UTC')
				.add(totalDuration, 'minute')
				.utc()
				.toISOString()

			const { error } = await supabase
				.from('appointments')
				.update({
					status: 'completed',
					add_ons: appointmentAddOnsData.length > 0 ? appointmentAddOnsData : null,
					end_ts: newEndTs,
				})
				.eq('id', appointment.id)
			if (error) throw error
		}

		// Checkout completes the appointment's open invoice (the 'pending'
		// payments row created with the appointment) so it keeps its invoice
		// number. Appointments from before the invoice flow have no pending
		// row — insert the payment directly, as before.
		let pendingQuery = supabase
			.from('payments')
			.select('id')
			.eq('payment_status', 'pending')
			.order('created_at', { ascending: false })
			.limit(1)
		pendingQuery = appointment.group_id
			? pendingQuery.eq('group_id', appointment.group_id)
			: pendingQuery.eq('appointment_id', appointment.id)
		const { data: pendingInvoice, error: pendingError } = await pendingQuery.maybeSingle()
		if (pendingError) throw pendingError

		const { error: paymentError } = pendingInvoice
			? await supabase.from('payments').update(paymentData).eq('id', pendingInvoice.id)
			: await supabase.from('payments').insert(paymentData)

		if (paymentError) throw paymentError

		if (isPostHogConfigured) {
			posthog.capture('payment_logged', {
				payment_method: paymentMethod,
				total_amount: totalAmount,
				tax_amount: taxAmount,
				tips_amount: tipsAmountCents,
				discount_amount: discountAmountCents,
				is_group_payment: Boolean(appointment.group_id),
			})
		}
	}, [
		appointment, merchant, addOnCounts, addOns, legacyAddOns, paymentMethod,
		serviceAmount, discountAmountCents, isFixedDiscount, customDiscountValue,
		discountPercent, tipsAmountCents, taxAmount, totalAmount,
		notes, groupedAppointments, serviceInfo,
	])

	useEffect(() => {
		savePaymentToDbRef.current = savePaymentToDb
	}, [savePaymentToDb])

	const handleSavePayment = async () => {
		setSaving(true)
		setErrorMessage('')
		try {
			await savePaymentToDb()
			finalizePayment()
		} catch (error) {
			console.error('Error saving payment:', error)
			setErrorMessage(error.message || 'Failed to save payment')
		} finally {
			setSaving(false)
		}
	}

	const handleOpenSquareCard = () => {
		setErrorMessage('')
		const apptPrefix = (appointment?.id || 'na').replace(/-/g, '').slice(0, 8)
		setSquareCardIdempotencyKey(`${apptPrefix}-${Date.now().toString(36)}`)
		setSquareCardOpen(true)
	}

	const handleSquareCardChargeSuccess = async () => {
		setSquareCardOpen(false)
		setSaving(true)
		try {
			await savePaymentToDb()
			finalizePayment()
		} catch (error) {
			console.error('Error saving payment after Square card charge:', error)
			setErrorMessage('Card was charged but the payment record failed to save. Please contact support.')
		} finally {
			setSaving(false)
		}
	}

	const handleChargeTerminal = async () => {
		setTerminalStatus('waiting')
		setErrorMessage('')

		try {
			const { data, error } = await supabase.functions.invoke('square-terminal-checkout', {
				body: {
					merchantId: merchant.id,
					amountCents: totalAmount,
					idempotencyKey: `${appointment.id}-${Date.now()}`,
				},
			})

			if (error || data?.error) {
				setTerminalStatus(null)
				let msg = data?.error || error?.message || 'Failed to start terminal checkout.'
				if (data?.detail) msg += ` (${data.detail})`
				setErrorMessage(msg)
				return
			}

			const { checkoutId } = data

			pollIntervalRef.current = setInterval(async () => {
				try {
					const { data: statusData, error: statusError } = await supabase.functions.invoke('square-checkout-status', {
						body: { merchantId: merchant.id, checkoutId },
					})

					if (statusError || statusData?.error) return

					if (statusData.status === 'COMPLETED') {
						stopPolling()
						setTerminalStatus('saving')
						try {
							await savePaymentToDbRef.current()
							finalizePaymentRef.current()
						} catch (err) {
							setTerminalStatus(null)
							setErrorMessage('Payment was received by the terminal but failed to save. Please contact support.')
						}
					} else if (statusData.status === 'CANCELED') {
						stopPolling()
						setTerminalStatus(null)
						setErrorMessage('Payment was cancelled on the terminal.')
					}
				} catch {
					// ignore transient network errors
				}
			}, TERMINAL_POLL_INTERVAL_MS)

			pollTimeoutRef.current = setTimeout(() => {
				stopPolling()
				setTerminalStatus(null)
				setErrorMessage('Terminal payment timed out. Please try again.')
			}, TERMINAL_TIMEOUT_MS)
		} catch (err) {
			setTerminalStatus(null)
			setErrorMessage(err.message || 'Unexpected error starting terminal checkout.')
		}
	}

	const handleCancelTerminalPayment = () => {
		stopPolling()
		setTerminalStatus(null)
	}

	const isFormLocked = Boolean(terminalStatus) || saving || squareCardOpen

	// ── Mobile: full-screen checkout per the "Mobile · checkout step" design ──
	if (isMobile) {
		const serviceLines = groupedAppointments.length > 0
			? groupedAppointments.map(a => ({
				key: a.id,
				pet: a.pets?.name || null,
				name: a.service_types?.name || 'Service',
				price: getServicePrice(a),
			}))
			: [{
				key: 'svc',
				pet: appointment?.pets?.name || null,
				name: serviceInfo?.name || appointment?.service_types?.name || 'Service',
				price: serviceAmount,
			}]

		const heroSub = [
			serviceLines.map(l => l.pet).filter(Boolean).join(' & ') || null,
			`appt #${appointment?.id?.slice(0, 8) || '—'}`,
			appointment?.start_ts
				? dayjs(appointment.start_ts).tz(merchant?.timezone || 'UTC').format('ddd, MMM D · h:mm A')
				: null,
		].filter(Boolean).join(' · ')

		// Closing after a successful payment still notifies the parent so the
		// details screen behind flips to Completed.
		const handleMobileClose = () => {
			if (paidInfo) onPaymentLogged?.()
			onClose()
		}

		// Full-screen takeover matching AppointmentDetailsDialog / AddAppointmentDialog's
		// mobile pattern (Drawer anchor="bottom", 100% height, square corners) — NOT a
		// MUI Dialog. Stacking two Dialogs/Drawers of different component types produced
		// mismatched backdrop/z-index behavior that let taps here fall through to the
		// appointment-details Drawer underneath; using the same component type keeps both
		// on the same stacking layer (later-mounted Drawer paints on top) and matches the
		// full-width presentation of its siblings.
		return (
			<Drawer
				anchor="bottom"
				open={open}
				onClose={(isFormLocked && !paidInfo) ? undefined : handleMobileClose}
				PaperProps={{
					sx: {
						height: '100%',
						maxHeight: '100%',
						borderRadius: 0,
						bgcolor: L.bg,
					}
				}}
			>
				<MobileCheckout ck={{
					heroSub,
					serviceLines,
					loading,
					saving,
					errorMessage,
					terminalStatus,
					addOns,
					addOnCounts,
					setAddOnCounts,
					legacyAddOns,
					onRemoveLegacy: (key) => setLegacyAddOns(prev => prev.filter(x => x.key !== key)),
					addOnSearch,
					setAddOnSearch,
					availablePaymentMethods,
					paymentMethod,
					setPaymentMethod,
					selectedTipOption,
					setSelectedTipOption,
					tipUnit,
					setTipUnit,
					customTipAmount,
					setCustomTipAmount,
					selectedDiscountOption,
					setSelectedDiscountOption,
					discountUnit,
					setDiscountUnit,
					customDiscountValue,
					setCustomDiscountValue,
					discountAmountCents,
					discountedSubtotal,
					discountLabel,
					notes,
					setNotes,
					subtotal,
					addOnsTotal,
					taxRate,
					taxAmount,
					tipsAmountCents,
					totalAmount,
					formatCurrency,
					cs,
					isFormLocked,
					isSquareTerminalMethod,
					isSquareCardMethod,
					paidInfo,
					onComplete: handleSavePayment,
					onChargeTerminal: handleChargeTerminal,
					onOpenSquareCard: handleOpenSquareCard,
					onCancelTerminal: handleCancelTerminalPayment,
					onBack: handleMobileClose,
					onDone: handleMobileClose,
				}} />

				<SquareCardDialog
					open={squareCardOpen}
					onClose={() => setSquareCardOpen(false)}
					merchant={merchant}
					client={client}
					amountCents={totalAmount}
					idempotencyKey={squareCardIdempotencyKey}
					note={`Appointment ${appointment?.id || ''}`.trim()}
					onChargeSuccess={handleSquareCardChargeSuccess}
				/>
			</Drawer>
		)
	}

	return (
		<Dialog
			open={open}
			onClose={isFormLocked ? undefined : onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					bgcolor: L.bg,
					color: L.text,
					fontFamily: L.font,
					borderRadius: '20px',
					overflow: 'hidden',
				}
			}}
		>
			{/* Header */}
			<Box sx={{
				px: '22px', pt: '20px', pb: '14px',
				display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px',
				borderBottom: `1px solid ${L.border}`,
			}}>
				<Box>
					<Typography sx={{ fontSize: '19px', fontWeight: 700, letterSpacing: '-0.01em', color: L.text, fontFamily: L.font, m: 0 }}>
						Log Payment
					</Typography>
					<Typography sx={{ fontSize: '12.5px', color: L.muted, mt: '2px', fontFamily: L.font }}>
						Appointment #{appointment?.id?.slice(0, 8) || '—'}
					</Typography>
				</Box>
				<IconButton
					size="small"
					onClick={isFormLocked ? undefined : onClose}
					disabled={isFormLocked}
					sx={{
						width: 28, height: 28, borderRadius: '50%',
						border: `1px solid ${L.border}`, bgcolor: L.surface,
						color: L.muted, flexShrink: 0,
						'&:hover': { color: L.text, borderColor: L.muted },
					}}
				>
					<CloseIcon sx={{ fontSize: 15 }} />
				</IconButton>
			</Box>

			<DialogContent sx={{ p: 0, '&.MuiDialogContent-root': { overflowY: 'auto' } }}>
				<Stack spacing={0} sx={{ px: '22px', pt: '4px', pb: '18px', gap: '18px' }}>
					{errorMessage && (
						<Alert
							severity="error"
							sx={{
								borderRadius: L.rSm, fontFamily: L.font, fontSize: '13.5px',
								bgcolor: '#FFF0F0', color: '#8B1A1A', border: '1px solid #FECACA',
								'& .MuiAlert-icon': { color: '#EF4444' },
							}}
						>
							{errorMessage}
						</Alert>
					)}

					{terminalStatus === 'waiting' && (
						<Alert
							severity="info"
							icon={<CircularProgress size={16} sx={{ color: L.primary }} />}
							sx={{
								borderRadius: L.rSm, fontFamily: L.font, fontSize: '13.5px',
								bgcolor: L.primarySoft, color: L.primaryDeep,
								border: `1px solid ${L.border}`,
								'& .MuiAlert-icon': { alignItems: 'center' },
							}}
						>
							Waiting for customer to pay on the Square Terminal…
						</Alert>
					)}
					{terminalStatus === 'saving' && (
						<Alert
							severity="success"
							icon={<CircularProgress size={16} sx={{ color: L.primary }} />}
							sx={{
								borderRadius: L.rSm, fontFamily: L.font, fontSize: '13.5px',
								bgcolor: '#F0FDF4', color: '#166534',
								'& .MuiAlert-icon': { alignItems: 'center' },
							}}
						>
							Payment confirmed! Saving record…
						</Alert>
					)}

					{/* Service */}
					<Box>
						<SectionTitle>{groupedAppointments.length > 1 ? 'Services' : 'Service'}</SectionTitle>
						{loading ? (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
								<CircularProgress size={14} sx={{ color: L.primary }} />
								<Typography sx={{ fontSize: '13px', color: L.muted, fontFamily: L.font }}>Loading…</Typography>
							</Box>
						) : groupedAppointments.length > 0 ? (
							<Stack spacing='8px'>
								{groupedAppointments.map((appt) => (
									<Box key={appt.id} sx={{
										display: 'flex', justifyContent: 'space-between', alignItems: 'center',
										bgcolor: L.surface, border: `1px solid ${L.border}`,
										borderRadius: L.r, px: '14px', py: '12px', gap: '10px',
									}}>
										<Box>
											<Typography sx={{ fontSize: '14.5px', fontWeight: 650, fontFamily: L.font, color: L.text }}>
												{appt.service_types?.name || 'Service'}
											</Typography>
											{appt.pets?.name && (
												<Typography sx={{ fontSize: '12px', color: L.muted, fontFamily: L.font, mt: '2px' }}>
													{appt.pets.name}
												</Typography>
											)}
										</Box>
										<Typography sx={{ fontSize: '15px', fontWeight: 750, fontFamily: L.font, color: L.text, flexShrink: 0 }}>
											{formatCurrency(getServicePrice(appt))}
										</Typography>
									</Box>
								))}
								{groupedAppointments.length > 1 && (
									<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', pt: '10px', borderTop: `1.5px dashed ${L.border}` }}>
										<Typography sx={{ fontSize: '13px', color: L.muted, fontFamily: L.font }}>Services Total</Typography>
										<Typography sx={{ fontSize: '13.5px', fontWeight: 700, fontFamily: L.font, color: L.text }}>
											{formatCurrency(serviceAmount)}
										</Typography>
									</Box>
								)}
							</Stack>
						) : (
							<Box sx={{
								display: 'flex', justifyContent: 'space-between', alignItems: 'center',
								bgcolor: L.surface, border: `1px solid ${L.border}`,
								borderRadius: L.r, px: '14px', py: '12px', gap: '10px',
							}}>
								<Typography sx={{ fontSize: '14.5px', fontWeight: 650, fontFamily: L.font, color: L.text }}>
									{serviceInfo?.name || appointment?.service_types?.name || 'Service'}
								</Typography>
								<Typography sx={{ fontSize: '15px', fontWeight: 750, fontFamily: L.font, color: L.text, flexShrink: 0 }}>
									{formatCurrency(serviceAmount)}
								</Typography>
							</Box>
						)}
					</Box>

					{/* Add-ons */}
					<Box>
						<SectionTitle hint="optional">Add-ons</SectionTitle>
						{/* Add-ons carried over from the booking that no longer match the
						    catalog — charged at their booked price, removable. */}
						{legacyAddOns.length > 0 && (
							<Stack spacing='6px' sx={{ mb: '8px' }}>
								{legacyAddOns.map((l) => (
									<Box key={l.key} sx={{
										display: 'flex', alignItems: 'center', justifyContent: 'space-between',
										p: '8px 12px', borderRadius: L.rSm, gap: '10px',
										bgcolor: L.primarySoft, border: `1.5px solid ${L.primary}`,
										opacity: isFormLocked ? 0.6 : 1,
									}}>
										<Box sx={{ flex: 1, minWidth: 0 }}>
											<Typography sx={{
												fontSize: '13.5px', fontWeight: 600, fontFamily: L.font, color: L.text,
												overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
											}}>
												{l.name}
											</Typography>
											<Typography sx={{ fontSize: '11.5px', color: L.muted, fontFamily: L.font, mt: '1px' }}>
												{formatCurrency(l.price)} · from booking
											</Typography>
										</Box>
										<IconButton
											size="small"
											aria-label={`Remove ${l.name}`}
											onClick={() => setLegacyAddOns(prev => prev.filter(x => x.key !== l.key))}
											disabled={isFormLocked}
											sx={{
												width: 27, height: 27, borderRadius: '50%', p: 0,
												border: `1.5px solid ${L.border}`, bgcolor: L.field,
												color: L.muted, flexShrink: 0,
												'&:disabled': { opacity: 0.35 },
											}}
										>
											<CloseIcon sx={{ fontSize: 14 }} />
										</IconButton>
									</Box>
								))}
							</Stack>
						)}
						{loading ? (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
								<CircularProgress size={14} sx={{ color: L.primary }} />
								<Typography sx={{ fontSize: '13px', color: L.muted, fontFamily: L.font }}>Loading…</Typography>
							</Box>
						) : addOns.length === 0 ? (
							<Typography sx={{ fontSize: '13px', color: L.muted, fontFamily: L.font }}>No add-ons available</Typography>
						) : (
							<Stack spacing='8px'>
								{/* Search */}
								<Box sx={{
									display: 'flex', alignItems: 'center', gap: '8px',
									border: `1.5px solid ${L.border}`, borderRadius: L.rSm,
									px: '12px', height: '40px', bgcolor: L.field,
									'&:focus-within': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
								}}>
									<SearchIcon sx={{ fontSize: 16, color: L.muted, flexShrink: 0 }} />
									<Box
										component="input"
										value={addOnSearch}
										onChange={(e) => setAddOnSearch(e.target.value)}
										disabled={isFormLocked}
										placeholder="Search add-ons…"
										sx={{
											border: 'none', outline: 'none', flex: 1, minWidth: 0,
											fontFamily: L.font, fontSize: '13.5px',
											bgcolor: 'transparent', color: L.text,
											'&::placeholder': { color: L.muted },
											'&:disabled': { opacity: 0.5 },
										}}
									/>
								</Box>

								{/* List */}
								<Box sx={{ maxHeight: '180px', overflowY: 'auto', pr: '3px', scrollbarWidth: 'thin' }}>
									<Stack spacing='6px'>
										{addOns
											.filter(addon => addon.name.toLowerCase().includes(addOnSearch.toLowerCase()))
											.map(addon => {
												const count = addOnCounts[addon.id] || 0
												return (
													<Box
														key={addon.id}
														sx={{
															display: 'flex', alignItems: 'center', justifyContent: 'space-between',
															p: '8px 12px', borderRadius: L.rSm, gap: '10px',
															bgcolor: count > 0 ? L.primarySoft : L.surface,
															border: `1.5px solid ${count > 0 ? L.primary : 'transparent'}`,
															opacity: isFormLocked ? 0.6 : 1,
														}}
													>
														<Box sx={{ flex: 1, minWidth: 0 }}>
															<Typography sx={{
																fontSize: '13.5px', fontWeight: count > 0 ? 600 : 400,
																fontFamily: L.font, color: L.text,
																overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
															}}>
																{addon.name}
															</Typography>
															<Typography sx={{ fontSize: '11.5px', color: L.muted, fontFamily: L.font, mt: '1px' }}>
																{formatCurrency(addon.price)} each
															</Typography>
														</Box>
														<Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
															<IconButton
																size="small"
																onClick={() => setAddOnCounts(prev => ({
																	...prev,
																	[addon.id]: Math.max(0, (prev[addon.id] || 0) - 1),
																}))}
																disabled={count === 0 || isFormLocked}
																sx={{
																	width: 27, height: 27, borderRadius: '50%', p: 0,
																	border: `1.5px solid ${L.border}`, bgcolor: L.field,
																	color: L.muted,
																	'&:disabled': { opacity: 0.35 },
																}}
															>
																<RemoveIcon sx={{ fontSize: 14 }} />
															</IconButton>
															<Typography sx={{
																fontSize: '13.5px', fontWeight: 700, fontFamily: L.font,
																minWidth: '16px', textAlign: 'center', fontVariantNumeric: 'tabular-nums',
																color: L.text,
															}}>
																{count}
															</Typography>
															<IconButton
																size="small"
																onClick={() => setAddOnCounts(prev => ({
																	...prev,
																	[addon.id]: (prev[addon.id] || 0) + 1,
																}))}
																disabled={isFormLocked}
																sx={{
																	width: 27, height: 27, borderRadius: '50%', p: 0,
																	border: 'none', bgcolor: L.primary, color: '#FFFFFF',
																	'&:hover': { bgcolor: L.primaryDeep },
																}}
															>
																<AddIcon sx={{ fontSize: 14 }} />
															</IconButton>
														</Box>
													</Box>
												)
											})}
									</Stack>
								</Box>

								{addOnsTotal > 0 && (
									<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', pt: '10px', borderTop: `1.5px dashed ${L.border}`, mt: '10px' }}>
										<Typography sx={{ fontSize: '13px', color: L.muted, fontFamily: L.font }}>Add-ons Total</Typography>
										<Typography sx={{ fontSize: '13.5px', fontWeight: 700, fontFamily: L.font, color: L.text }}>
											{formatCurrency(addOnsTotal)}
										</Typography>
									</Box>
								)}
							</Stack>
						)}
					</Box>

					{/* Discount */}
					<Box>
						<SectionTitle hint="optional">Discount</SectionTitle>
						<Stack spacing='8px'>
							<Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
								{['10', '15', '25'].map((pct) => (
									<Box
										key={pct}
										component="button"
										type="button"
										onClick={() => setSelectedDiscountOption(selectedDiscountOption === pct ? null : pct)}
										disabled={isFormLocked}
										sx={{
											flex: '1 1 0', minWidth: 0, border: `1.5px solid ${selectedDiscountOption === pct ? L.primary : L.border}`,
											borderRadius: L.rSm, p: '8px 4px',
											display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px',
											bgcolor: selectedDiscountOption === pct ? L.primarySoft : L.field,
											cursor: 'pointer', fontFamily: L.font,
											transition: 'border-color .12s, background .12s',
											'&:hover:not(:disabled)': { borderColor: L.primary },
											'&:disabled': { opacity: 0.5, cursor: 'default' },
										}}
									>
										<Typography sx={{
											fontSize: '13px', fontWeight: 700,
											color: selectedDiscountOption === pct ? L.primaryDeep : L.text,
											fontFamily: L.font, whiteSpace: 'nowrap',
										}}>
											{pct}% off
										</Typography>
										<Typography sx={{
											fontSize: '11px',
											color: selectedDiscountOption === pct ? L.primary : L.muted,
											fontFamily: L.font, whiteSpace: 'nowrap',
										}}>
											−{formatCurrency(Math.round(subtotal * Number(pct) / 100))}
										</Typography>
									</Box>
								))}
								<Box
									component="button"
									type="button"
									onClick={() => setSelectedDiscountOption(selectedDiscountOption === 'custom' ? null : 'custom')}
									disabled={isFormLocked}
									sx={{
										flex: '1 1 0', minWidth: 0, border: `1.5px solid ${selectedDiscountOption === 'custom' ? L.primary : L.border}`,
										borderRadius: L.rSm, p: '8px 4px',
										display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px', justifyContent: 'center',
										bgcolor: selectedDiscountOption === 'custom' ? L.primarySoft : L.field,
										cursor: 'pointer', fontFamily: L.font,
										transition: 'border-color .12s, background .12s',
										'&:hover:not(:disabled)': { borderColor: L.primary },
										'&:disabled': { opacity: 0.5, cursor: 'default' },
									}}
								>
									<Typography sx={{
										fontSize: '13px', fontWeight: 700,
										color: selectedDiscountOption === 'custom' ? L.primaryDeep : L.text,
										fontFamily: L.font, whiteSpace: 'nowrap',
									}}>
										Custom
									</Typography>
									<Typography sx={{
										fontSize: '11px',
										color: selectedDiscountOption === 'custom' ? L.primary : L.muted,
										fontFamily: L.font, whiteSpace: 'nowrap',
									}}>
										{selectedDiscountOption === 'custom' && discountAmountCents > 0
											? `−${formatCurrency(discountAmountCents)}`
											: `% or ${cs}`}
									</Typography>
								</Box>
							</Box>

							{selectedDiscountOption === 'custom' && (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									{/* percent / fixed unit toggle */}
									<Box role="group" aria-label="Percent or dollar discount" sx={{
										display: 'inline-flex', gap: '2px', height: '38px', alignItems: 'center',
										bgcolor: L.surface, border: `1.5px solid ${L.border}`,
										borderRadius: L.rSm, px: '3px', flexShrink: 0,
									}}>
										{[['percent', '%'], ['fixed', cs]].map(([unit, symbol]) => (
											<Box
												key={unit}
												component="button"
												type="button"
												onClick={() => setDiscountUnit(unit)}
												disabled={isFormLocked}
												sx={{
													border: 'none', width: '30px', height: '28px', borderRadius: '7px',
													fontFamily: L.font, fontSize: '13px', fontWeight: 750, cursor: 'pointer',
													display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
													bgcolor: discountUnit === unit ? '#FFFFFF' : 'transparent',
													color: discountUnit === unit ? L.primaryDeep : L.muted,
													boxShadow: discountUnit === unit ? '0 1px 2px rgba(46,42,64,0.16)' : 'none',
													'&:disabled': { opacity: 0.5, cursor: 'default' },
												}}
											>
												{symbol}
											</Box>
										))}
									</Box>
									<Box sx={{
										width: '100px', display: 'flex', alignItems: 'center', gap: '5px', height: '38px',
										border: `1.5px solid ${L.border}`, borderRadius: L.rSm, bgcolor: L.field,
										px: '11px', flexShrink: 0,
										'&:focus-within': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
									}}>
										{discountUnit === 'fixed' && (
											<Typography sx={{ fontSize: '13px', fontWeight: 700, color: L.muted, fontFamily: L.font, flexShrink: 0 }}>{cs}</Typography>
										)}
										<Box
											component="input"
											type="text"
											inputMode="decimal"
											placeholder="0"
											value={customDiscountValue}
											onChange={(e) => setCustomDiscountValue(e.target.value.replace(/[^\d.]/g, ''))}
											disabled={isFormLocked}
											autoFocus
											sx={{
												width: '100%', minWidth: 0, border: 'none', outline: 'none',
												bgcolor: 'transparent', fontFamily: L.font, fontSize: '13.5px',
												fontWeight: 650, color: L.text,
												'&::placeholder': { color: L.muted, fontWeight: 500 },
												'&:disabled': { opacity: 0.5 },
											}}
										/>
										{discountUnit === 'percent' && (
											<Typography sx={{ fontSize: '13px', fontWeight: 700, color: L.muted, fontFamily: L.font, flexShrink: 0 }}>%</Typography>
										)}
									</Box>
									<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '5px', minWidth: 0, whiteSpace: 'nowrap' }}>
										{discountAmountCents > 0 && (
											<Typography sx={{
												fontSize: '13.5px', fontWeight: 750, color: '#2F8A5B',
												fontFamily: L.font, fontVariantNumeric: 'tabular-nums',
											}}>
												−{formatCurrency(discountAmountCents)}
											</Typography>
										)}
										<Typography sx={{ fontSize: '12px', fontWeight: 600, color: L.muted, fontFamily: L.font }}>
											off subtotal
										</Typography>
									</Box>
								</Box>
							)}
						</Stack>
					</Box>

					{/* Payment Method */}
					<Box>
						<SectionTitle>Payment method</SectionTitle>
						<FormControl fullWidth disabled={isFormLocked}>
							<Select
								value={paymentMethod}
								onChange={(e) => setPaymentMethod(e.target.value)}
								sx={{
									fontFamily: L.font, fontSize: '14px', fontWeight: 600,
									borderRadius: L.rSm, bgcolor: L.field,
									'& .MuiOutlinedInput-notchedOutline': { borderColor: L.border, borderWidth: '1.5px' },
									'&:hover .MuiOutlinedInput-notchedOutline': { borderColor: L.primary },
									'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
										borderColor: L.primary, borderWidth: '1.5px',
										boxShadow: `0 0 0 3px ${L.primarySoft}`,
									},
									'& .MuiSelect-select': { py: '10px', color: L.text },
									'& .MuiSvgIcon-root': { color: L.muted },
								}}
								MenuProps={{
									PaperProps: {
										sx: {
											bgcolor: '#FFFFFF', borderRadius: '13px',
											border: `1px solid ${L.border}`,
											boxShadow: `0 14px 36px rgba(95, 78, 194, 0.16), 0 3px 10px rgba(95, 78, 194, 0.08)`,
											p: '6px',
											'& .MuiList-root': { p: 0 },
										}
									}
								}}
							>
								{availablePaymentMethods.map((method) => (
									<MenuItem
										key={method.value}
										value={method.value}
										sx={{
											fontFamily: L.font, fontSize: '13.5px', fontWeight: 600,
											borderRadius: '9px', color: L.text, height: '42px',
											'&.Mui-selected': { bgcolor: L.primarySoft, color: L.primaryDeep },
											'&.Mui-selected:hover': { bgcolor: L.primarySoft },
											'&:hover': { bgcolor: L.surface },
										}}
									>
										{method.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>

					{/* Tip */}
					<Box>
						<SectionTitle hint="optional">Tip</SectionTitle>
						<Stack spacing='8px'>
							<Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
								{['15', '18', '25'].map((pct) => (
									<Box
										key={pct}
										component="button"
										type="button"
										onClick={() => setSelectedTipOption(selectedTipOption === pct ? null : pct)}
										disabled={isFormLocked}
										sx={{
											flex: '1 1 0', minWidth: 0, border: `1.5px solid ${selectedTipOption === pct ? L.primary : L.border}`,
											borderRadius: L.rSm, p: '8px 4px',
											display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px',
											bgcolor: selectedTipOption === pct ? L.primarySoft : L.field,
											cursor: 'pointer', fontFamily: L.font,
											transition: 'border-color .12s, background .12s',
											'&:hover:not(:disabled)': { borderColor: L.primary },
											'&:disabled': { opacity: 0.5, cursor: 'default' },
										}}
									>
										<Typography sx={{
											fontSize: '13px', fontWeight: 700,
											color: selectedTipOption === pct ? L.primaryDeep : L.text,
											fontFamily: L.font, whiteSpace: 'nowrap',
										}}>
											{pct}%
										</Typography>
										<Typography sx={{
											fontSize: '11px',
											color: selectedTipOption === pct ? L.primary : L.muted,
											fontFamily: L.font, whiteSpace: 'nowrap',
										}}>
											{formatCurrency(Math.round(discountedSubtotal * Number(pct) / 100))}
										</Typography>
									</Box>
								))}
								<Box
									component="button"
									type="button"
									onClick={() => setSelectedTipOption(selectedTipOption === 'custom' ? null : 'custom')}
									disabled={isFormLocked}
									sx={{
										flex: '1 1 0', minWidth: 0, border: `1.5px solid ${selectedTipOption === 'custom' ? L.primary : L.border}`,
										borderRadius: L.rSm, p: '8px 4px',
										display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
										bgcolor: selectedTipOption === 'custom' ? L.primarySoft : L.field,
										cursor: 'pointer', fontFamily: L.font,
										transition: 'border-color .12s, background .12s',
										'&:hover:not(:disabled)': { borderColor: L.primary },
										'&:disabled': { opacity: 0.5, cursor: 'default' },
									}}
								>
									<Typography sx={{
										fontSize: '13px', fontWeight: 700,
										color: selectedTipOption === 'custom' ? L.primaryDeep : L.text,
										fontFamily: L.font,
									}}>
										Custom
									</Typography>
									<Typography sx={{
										fontSize: '11px',
										color: selectedTipOption === 'custom' ? L.primary : L.muted,
										fontFamily: L.font, whiteSpace: 'nowrap',
									}}>
										{selectedTipOption === 'custom' && tipsAmountCents > 0
											? formatCurrency(tipsAmountCents)
											: `% or ${cs}`}
									</Typography>
								</Box>
							</Box>

							{selectedTipOption === 'custom' && (
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
									{/* percent / fixed unit toggle */}
									<Box role="group" aria-label="Percent or dollar tip" sx={{
										display: 'inline-flex', gap: '2px', height: '38px', alignItems: 'center',
										bgcolor: L.surface, border: `1.5px solid ${L.border}`,
										borderRadius: L.rSm, px: '3px', flexShrink: 0,
									}}>
										{[['percent', '%'], ['fixed', cs]].map(([unit, symbol]) => (
											<Box
												key={unit}
												component="button"
												type="button"
												onClick={() => setTipUnit(unit)}
												disabled={isFormLocked}
												sx={{
													border: 'none', width: '30px', height: '28px', borderRadius: '7px',
													fontFamily: L.font, fontSize: '13px', fontWeight: 750, cursor: 'pointer',
													display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
													bgcolor: tipUnit === unit ? '#FFFFFF' : 'transparent',
													color: tipUnit === unit ? L.primaryDeep : L.muted,
													boxShadow: tipUnit === unit ? '0 1px 2px rgba(46,42,64,0.16)' : 'none',
													'&:disabled': { opacity: 0.5, cursor: 'default' },
												}}
											>
												{symbol}
											</Box>
										))}
									</Box>
									<Box sx={{
										width: '100px', display: 'flex', alignItems: 'center', gap: '5px', height: '38px',
										border: `1.5px solid ${L.border}`, borderRadius: L.rSm, bgcolor: L.field,
										px: '11px', flexShrink: 0,
										'&:focus-within': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
									}}>
										{tipUnit === 'fixed' && (
											<Typography sx={{ fontSize: '13px', fontWeight: 700, color: L.muted, fontFamily: L.font, flexShrink: 0 }}>{cs}</Typography>
										)}
										<Box
											component="input"
											type="text"
											inputMode="decimal"
											placeholder="0"
											value={customTipAmount}
											onChange={(e) => setCustomTipAmount(e.target.value.replace(/[^\d.]/g, ''))}
											disabled={isFormLocked}
											autoFocus
											sx={{
												width: '100%', minWidth: 0, border: 'none', outline: 'none',
												bgcolor: 'transparent', fontFamily: L.font, fontSize: '13.5px',
												fontWeight: 650, color: L.text,
												'&::placeholder': { color: L.muted, fontWeight: 500 },
												'&:disabled': { opacity: 0.5 },
											}}
										/>
										{tipUnit === 'percent' && (
											<Typography sx={{ fontSize: '13px', fontWeight: 700, color: L.muted, fontFamily: L.font, flexShrink: 0 }}>%</Typography>
										)}
									</Box>
									<Box sx={{ display: 'flex', alignItems: 'baseline', gap: '5px', minWidth: 0, whiteSpace: 'nowrap' }}>
										{tipsAmountCents > 0 && (
											<Typography sx={{
												fontSize: '13.5px', fontWeight: 750, color: L.primaryDeep,
												fontFamily: L.font, fontVariantNumeric: 'tabular-nums',
											}}>
												+{formatCurrency(tipsAmountCents)}
											</Typography>
										)}
										<Typography sx={{ fontSize: '12px', fontWeight: 600, color: L.muted, fontFamily: L.font }}>
											added as tip
										</Typography>
									</Box>
								</Box>
							)}
						</Stack>
					</Box>

					{/* Notes */}
					<Box>
						<Box
							component="textarea"
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							placeholder="Add any notes about this payment…"
							disabled={isFormLocked}
							rows={2}
							sx={{
								width: '100%', border: `1.5px solid ${L.border}`, borderRadius: L.rSm,
								px: '12px', py: '10px', fontFamily: L.font, fontSize: '13.5px',
								resize: 'vertical', bgcolor: L.field, minHeight: '56px',
								color: L.text, outline: 'none', boxSizing: 'border-box',
								'&::placeholder': { color: L.muted },
								'&:focus': { borderColor: L.primary, boxShadow: `0 0 0 3px ${L.primarySoft}` },
								'&:disabled': { opacity: 0.5 },
							}}
						/>
					</Box>

					{/* Summary */}
					<Box>
						<SectionTitle>Summary</SectionTitle>
						<Stack spacing='7px'>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
								<Typography sx={{ fontSize: '13.5px', color: L.muted, fontFamily: L.font }}>Subtotal</Typography>
								<Typography sx={{ fontSize: '13.5px', fontFamily: L.font, color: L.text, fontVariantNumeric: 'tabular-nums' }}>
									{formatCurrency(subtotal)}
								</Typography>
							</Box>
							{discountAmountCents > 0 && (
								<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
									<Typography sx={{ fontSize: '13.5px', color: L.primaryDeep, fontWeight: 650, fontFamily: L.font }}>{discountLabel}</Typography>
									<Typography sx={{ fontSize: '13.5px', color: L.primaryDeep, fontWeight: 650, fontFamily: L.font, fontVariantNumeric: 'tabular-nums' }}>
										−{formatCurrency(discountAmountCents)}
									</Typography>
								</Box>
							)}
							<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
								<Typography sx={{ fontSize: '13.5px', color: L.muted, fontFamily: L.font }}>Tax ({taxRate}%)</Typography>
								<Typography sx={{ fontSize: '13.5px', fontFamily: L.font, color: L.text, fontVariantNumeric: 'tabular-nums' }}>
									{formatCurrency(taxAmount)}
								</Typography>
							</Box>
							{tipsAmountCents > 0 && (
								<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
									<Typography sx={{ fontSize: '13.5px', color: L.muted, fontFamily: L.font }}>{tipLabel}</Typography>
									<Typography sx={{ fontSize: '13.5px', fontFamily: L.font, color: L.text, fontVariantNumeric: 'tabular-nums' }}>
										{formatCurrency(tipsAmountCents)}
									</Typography>
								</Box>
							)}
							<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '10px', borderTop: `1.5px solid ${L.border}`, pt: '10px', mt: '3px' }}>
								<Typography sx={{ fontSize: '16px', fontWeight: 800, fontFamily: L.font, color: L.text }}>Total</Typography>
								<Typography sx={{ fontSize: '16px', fontWeight: 800, fontFamily: L.font, color: L.primaryDeep, fontVariantNumeric: 'tabular-nums' }}>
									{formatCurrency(totalAmount)}
								</Typography>
							</Box>
						</Stack>
					</Box>
				</Stack>
			</DialogContent>

			{/* Footer */}
			<Box sx={{
				px: '22px', py: '14px', pb: '18px',
				borderTop: `1px solid ${L.border}`,
				display: 'flex', gap: '10px', justifyContent: 'flex-end',
				bgcolor: L.bg,
			}}>
				{terminalStatus ? (
					<>
						<Box
							component="button"
							type="button"
							onClick={handleCancelTerminalPayment}
							disabled={terminalStatus === 'saving'}
							sx={{
								display: { xs: 'none', sm: 'flex' },
								alignItems: 'center', justifyContent: 'center',
								px: '16px', height: '42px', borderRadius: L.rSm,
								border: `1.5px solid ${L.border}`, bgcolor: 'transparent',
								fontSize: '14px', fontWeight: 650, color: L.muted,
								fontFamily: L.font, cursor: 'pointer',
								'&:hover:not(:disabled)': { color: L.text, borderColor: L.muted },
								'&:disabled': { opacity: 0.5, cursor: 'default' },
							}}
						>
							<Typography sx={{ fontSize: '14px', fontWeight: 650, color: L.muted, fontFamily: L.font }}>Cancel</Typography>
						</Box>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
							<CircularProgress size={16} sx={{ color: L.primary }} />
							<Typography sx={{ fontSize: '13.5px', color: L.muted, fontFamily: L.font }}>
								{terminalStatus === 'saving' ? 'Saving payment…' : 'Waiting for payment…'}
							</Typography>
						</Box>
					</>
				) : (
					<>
						<Box
							component="button"
							type="button"
							onClick={onClose}
							disabled={saving}
							sx={{
								display: { xs: 'none', sm: 'flex' },
								alignItems: 'center', justifyContent: 'center',
								px: '16px', height: '42px', borderRadius: L.rSm,
								border: `1.5px solid ${L.border}`, bgcolor: 'transparent',
								fontSize: '14px', fontWeight: 650, color: L.muted,
								fontFamily: L.font, cursor: 'pointer',
								'&:hover:not(:disabled)': { color: L.text, borderColor: L.muted },
								'&:disabled': { opacity: 0.5, cursor: 'default' },
							}}
						>
							<Typography sx={{ fontSize: '14px', fontWeight: 650, color: L.muted, fontFamily: L.font }}>Cancel</Typography>
						</Box>
						{isSquareTerminalMethod ? (
							<Box
								component="button"
								type="button"
								onClick={handleChargeTerminal}
								disabled={saving || totalAmount <= 0}
								sx={{
									display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
									height: '42px', px: '18px', borderRadius: L.rSm,
									border: 'none', bgcolor: L.primary, color: '#FFFFFF',
									fontSize: '14px', fontWeight: 700, fontFamily: L.font,
									boxShadow: `0 2px 8px ${L.primarySoft}`, cursor: 'pointer', whiteSpace: 'nowrap',
									'&:hover:not(:disabled)': { bgcolor: L.primaryDeep },
									'&:disabled': { opacity: 0.5, cursor: 'default' },
								}}
							>
								<PaymentIcon sx={{ fontSize: 16 }} />
								<span>Charge Terminal · {formatCurrency(totalAmount)}</span>
							</Box>
						) : isSquareCardMethod ? (
							<Box
								component="button"
								type="button"
								onClick={handleOpenSquareCard}
								disabled={saving || totalAmount <= 0}
								sx={{
									display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
									height: '42px', px: '18px', borderRadius: L.rSm,
									border: 'none', bgcolor: L.primary, color: '#FFFFFF',
									fontSize: '14px', fontWeight: 700, fontFamily: L.font,
									boxShadow: `0 2px 8px ${L.primarySoft}`, cursor: 'pointer', whiteSpace: 'nowrap',
									'&:hover:not(:disabled)': { bgcolor: L.primaryDeep },
									'&:disabled': { opacity: 0.5, cursor: 'default' },
								}}
							>
								{saving ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : <CreditCardIcon sx={{ fontSize: 16 }} />}
								<span>{saving ? 'Saving…' : `Complete Payment · ${formatCurrency(totalAmount)}`}</span>
							</Box>
						) : (
							<Box
								component="button"
								type="button"
								onClick={handleSavePayment}
								disabled={saving}
								sx={{
									display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
									height: '42px', px: '18px', borderRadius: L.rSm,
									border: 'none', bgcolor: L.primary, color: '#FFFFFF',
									fontSize: '14px', fontWeight: 700, fontFamily: L.font,
									boxShadow: `0 2px 8px ${L.primarySoft}`, cursor: 'pointer', whiteSpace: 'nowrap',
									'&:hover:not(:disabled)': { bgcolor: L.primaryDeep },
									'&:disabled': { opacity: 0.5, cursor: 'default' },
								}}
							>
								{saving ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} /> : <PaymentIcon sx={{ fontSize: 16 }} />}
								<span>{saving ? 'Processing…' : `Complete Payment · ${formatCurrency(totalAmount)}`}</span>
							</Box>
						)}
					</>
				)}
			</Box>

			<SquareCardDialog
				open={squareCardOpen}
				onClose={() => setSquareCardOpen(false)}
				merchant={merchant}
				client={client}
				amountCents={totalAmount}
				idempotencyKey={squareCardIdempotencyKey}
				note={`Appointment ${appointment?.id || ''}`.trim()}
				onChargeSuccess={handleSquareCardChargeSuccess}
			/>
		</Dialog>
	)
}
